import { useWriteErrorStore } from "@api/writeErrorStore";
import { MessageSlip } from "@scribenote/brian-design-system";

/**
 * The write-failure notice, bottom right: the pink "while you were out" slip
 * Brian leaves behind. Every mutation that fails without its own error UI lands
 * here via the `MutationCache` in {@link queryClient}, so a failed write is
 * never silent.
 *
 * One at a time by design: the store holds a single message, so this can never
 * become the stacked column of toasts it replaces (WEB-3319).
 *
 * It does not auto-dismiss. A failure the user hasn't read yet is a failure they
 * haven't received, and unlike the old toasts there's no pile-up to time out of
 * the way. Clicking anywhere on the slip dismisses it, so getting rid of it
 * never means aiming for a small target; the next failure also replaces it.
 *
 * `key={seq}` remounts on each new message so the slip's entrance replays and
 * `MessageSlip`'s own `role="alert"` fires again even when the text is
 * identical.
 *
 * The slip is compact and tilted, so it sits closer to the corner on narrow
 * viewports to stay clear of the edge; from `sm` up it keeps its usual inset.
 */
export const WriteErrorNotice = () => {
  const message = useWriteErrorStore((s) => s.message);
  const seq = useWriteErrorStore((s) => s.seq);
  const dismiss = useWriteErrorStore((s) => s.dismiss);

  if (!message) return null;

  return (
    <MessageSlip
      key={seq}
      message={message}
      onDismiss={dismiss}
      data-testid="write-error-notice"
      className="z-toast fixed right-4 bottom-4 sm:right-12 sm:bottom-6"
    />
  );
};
